import React, { useState, useEffect } from 'react';
import { Paper, Typography, Box, Card, CardContent, Grid, CircularProgress, Button, TextField, Collapse, IconButton } from '@mui/material';
import Localization from '../../Localization';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const StatisticDisplay = ({ icon, title, value }) => (
  <Grid item xs={12} md={4}>
    <Card elevation={3} sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      p: 3,
      transition: 'transform 0.3s, box-shadow 0.3s',
      '&:hover': {
        transform: 'translateY(-5px)',
        boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
      }
    }}>
      {icon}
      <Typography variant="h6" color="text.secondary" sx={{ mt: 1 }}>
        {title}
      </Typography>
      <Typography variant="h3" sx={{ mt: 1, fontWeight: 'bold', color: '#9c27b0' }}>
        {value}
      </Typography>
    </Card>
  </Grid>
);


const OverallAssessmentPage = ({ epackName, submissionDate, dateFilterStart, dateFilterEnd, isDateRangeFilter, selectedDates, submissionList, hasSubmissions, root, localeId, schoolId, tempDateFilterStart, tempDateFilterEnd, onDateFilterStartChange, onDateFilterEndChange, onConfirmDateFilter, onClearDateFilter }) => {
  const [studentCount, setStudentCount] = useState(0);
  const [submissionCount, setSubmissionCount] = useState(0);
  const [totalAnswers, setTotalAnswers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isDateFilterExpanded, setIsDateFilterExpanded] = useState(false);

  useEffect(() => {
    // 1. Calculate student count (unique students)
    if (submissionList && submissionList.length > 0) {
      const uniqueStudents = new Set(submissionList.map(sub => sub.user_name));
      setStudentCount(uniqueStudents.size);
    } else {
      setStudentCount(0);
    }
    
    // 2. Calculate total submissions
    setSubmissionCount(submissionList ? submissionList.length : 0);

    // 3. Fetch total answers
    if (localeId && submissionDate && schoolId) {
      setLoading(true);
      
      // Use the actual submission date for API calls
      let dateParam = submissionDate;
      
      const params = {
        locale_id: localeId,
        date: dateParam,
        school_id: schoolId
      };
      root.refs.API.request(
        "get_total_answers_number_by_epack_and_date",
        params,
        (res) => {
          try {
            const total = res.body && res.body.result ? (res.body.result.total || 0) : 0;
            setTotalAnswers(total);
          } finally {
            setLoading(false);
          }
        },
        (err) => {
          console.error('Failed to get total answers:', err);
          setTotalAnswers(0);
          setLoading(false);
        }
      );
    } else {
        setLoading(false);
    }

  }, [submissionList, localeId, submissionDate, schoolId, root]);

  // Helper function to format dates based on locale
  const formatDateByLocale = (dateString) => {
    const date = new Date(dateString);
    const currentLanguage = Localization.strings.getLanguage();
    if (currentLanguage === 'tc') { // Chinese format: YYYY年M月D日
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      return `${year}年${month}月${day}日`;
    } else { // English format: DD-MM-YYYY
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    }
  };

  // Get selected date display
  const getSelectedDateDisplay = () => {
    if (selectedDates && selectedDates.length > 1) { // Priority 1: Multiple timeline dates
      const sortedDates = [...selectedDates].sort((a, b) => new Date(a) - new Date(b));
      const startDate = formatDateByLocale(sortedDates[0]);
      const endDate = formatDateByLocale(sortedDates[sortedDates.length - 1]);
      return `${startDate} - ${endDate}`;
    }
    
    if (dateFilterStart && dateFilterEnd) { // Priority 2: Date range filter
      const startDate = formatDateByLocale(dateFilterStart);
      const endDate = formatDateByLocale(dateFilterEnd);
      return `${startDate} - ${endDate}`;
    }
    
    if (dateFilterStart && !dateFilterEnd) { // Only start date in filter
      return formatDateByLocale(dateFilterStart);
    }
    
    if (selectedDates && selectedDates.length === 1) { // Priority 3: Single timeline date
      return formatDateByLocale(selectedDates[0]);
    }
    
    if (submissionDate) { // Priority 4: Single submission date
      return formatDateByLocale(submissionDate);
    }
    
    return Localization.strings.NO_DATE_SELECTED || "未選擇日期";
  };

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      {/* Show "No answer submitted" message when no submissions exist */}
      {!hasSubmissions ? (
        <Card sx={{ p: 3, textAlign: 'center', mb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            {Localization.strings.EMPTY_SUBMISSION_REMINDER}
          </Typography>
        </Card>
      ) : (
        <>
          {/* Selected Date Display and Date Filter - Horizontal Layout */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 2
          }}>
            {/* Left side - Date Display */}
            <Typography variant="h6" sx={{ 
              fontWeight: '600',
              fontSize: '1.1rem',
              flexShrink: 0
            }}>
              {Localization.strings.FIELD_TRIP_DATE || "實地考察日期"}: {getSelectedDateDisplay()}
            </Typography>
            
            {/* Right side - Date Filter - Horizontal Expand/Collapse */}
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center',
                cursor: 'pointer',
                px: 1,
                py: 0.5,
                borderRadius: '4px',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setIsDateFilterExpanded(!isDateFilterExpanded)}
              >
                <Typography variant="body2" sx={{ mr: 1, color: '#9c27b0', fontWeight: '500' }}>
                  {Localization.strings.DATE_RANGE || "Date Range"}
                </Typography>
                <IconButton size="small" sx={{ 
                  transform: isDateFilterExpanded ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 0.3s',
                  color: '#9c27b0',
                  p: 0.5
                }}>
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* Horizontal Collapse/Expand for Date Filter Fields */}
              <Collapse in={isDateFilterExpanded} orientation="horizontal">
                <Box sx={{ 
                  ml: 2,
                  p: 1.5, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '8px',
                  bgcolor: 'background.paper',
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1.5,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  minWidth: 'fit-content'
                }}>
                  <TextField
                    type="date"
                    label={Localization.strings.FROM || "From"}
                    size="small"
                    value={tempDateFilterStart || ''}
                    onChange={(e) => onDateFilterStartChange && onDateFilterStartChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      minWidth: '120px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#9c27b0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9c27b0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#9c27b0',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#9c27b0',
                        '&.Mui-focused': {
                          color: '#9c27b0',
                        },
                      },
                    }}
                  />
                  
                  <TextField
                    type="date"
                    label={Localization.strings.TO || "To"}
                    size="small"
                    value={tempDateFilterEnd || ''}
                    onChange={(e) => onDateFilterEndChange && onDateFilterEndChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ 
                      minWidth: '120px',
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: '#9c27b0',
                        },
                        '&:hover fieldset': {
                          borderColor: '#9c27b0',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#9c27b0',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: '#9c27b0',
                        '&.Mui-focused': {
                          color: '#9c27b0',
                        },
                      },
                    }}
                  />
                  
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onConfirmDateFilter}
                    sx={{ 
                      minWidth: 'fit-content',
                      backgroundColor: '#9c27b0',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: '#7b1fa2',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                      }
                    }}
                  >
                    {Localization.strings.CONFIRM || "Confirm"}
                  </Button>
                  
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={onClearDateFilter}
                    sx={{ 
                      minWidth: 'fit-content',
                      borderColor: '#9c27b0',
                      color: '#9c27b0',
                      '&:hover': {
                        backgroundColor: '#9c27b0',
                        color: 'white',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                      }
                    }}
                  >
                    {Localization.strings.CLEAR || "Clear"}
                  </Button>
                </Box>
              </Collapse>
            </Box>
          </Box>
          
          {/* Overall Assessment Content */}
          <Box sx={{ 
            bgcolor: '#9c27b0', 
            color: 'white', 
            py: 1.5, 
            px: 3, 
            borderRadius: '8px 8px 0 0' 
          }}>
            <Typography variant="h6" sx={{ mb: 0 }}>
              {Localization.strings.OVERALL_ASSESSMENT_STATISTICS || '評估統計概覽'}
            </Typography>
          </Box>
      <Box sx={{
        minHeight: '800px',
        backgroundColor: 'white',
        p: 3,
        borderRadius: '0 0 8px 8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={4} justifyContent="center" alignItems="stretch">
            <StatisticDisplay
              icon={<PeopleIcon sx={{ fontSize: 50, color: '#ad1179' }} />}
              title={Localization.strings.STUDENT_COUNT || "總學生人數"}
              value={studentCount}
            />
            <StatisticDisplay
              icon={<DescriptionIcon sx={{ fontSize: 50, color: '#ad1179' }} />}
              title={Localization.strings.TOTAL_SUBMISSIONS || "總提交數量"}
              value={submissionCount}
            />
            <StatisticDisplay
              icon={<CheckCircleIcon sx={{ fontSize: 50, color: '#ad1179' }} />}
              title={Localization.strings.TOTAL_ANSWERS || "總答案數量"}
              value={totalAnswers}
            />
          </Grid>
        )}
      </Box>
        </>
      )}
    </Paper>
  );
};

export default OverallAssessmentPage; 